// eslint-disable-next-line
/*global fetch */
import { uniqBy, find } from "lodash";
import React from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { useDispatch, useSelector } from "react-redux";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { auth } from "./../firebase_config";

import { enqueueSnackbar } from "./../slices/notifications";
import errorReporter from '../utils/errorReporting';

const EndSubscription = () => {
  // const { user } = useSelector((state) => state.auth); // Assuming you have a Firebase auth hook
  const currentUser = auth.currentUser;
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const { subscriptions } = useSelector((state) => state.user);
  const activeSubscription = React.useMemo(
    () => find(subscriptions, (s) => s.status === "ACTIVE"),
    [subscriptions],
  );
  const pendingSubscription = React.useMemo(
    () => find(subscriptions, (s) => s.status === "PENDING"),
    [subscriptions],
  );
  const subscriptionId = activeSubscription?.id || pendingSubscription?.id;

  const formatDate = React.useCallback(
    (date) => {
      const dateParts = date.split("-");

      // Create the date object manually (Year, Month - 1, Day)
      const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

      // Formatting the date to "Nov 1, 2024"
      const formattedDate = dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      return formattedDate;
    },
    [subscriptions],
  );

  const handleCancel = async () => {
    setLoading(true);
    const response = await fetch(
      "https://us-central1-shelby-estates-hoa.cloudfunctions.net" +
        "/cancelSubscription",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await currentUser.getIdToken()}`, // Send Firebase token
        },
        body: JSON.stringify({
          subscriptionId,
        }),
      },
    );

    if (response.ok) {
      const result = await response.json();
      setLoading(false);
      dispatch(
        enqueueSnackbar({
          message: "Subscription Canceled Successfully",
          options: {
            variant: "success",
          },
        }),
      );
      console.log("Subscription cancel successful:", result);
    } else {
      const error = await response.json();
      
      // Report subscription cancellation error
      const errorObject = new Error(`Subscription cancel failed: ${JSON.stringify(error)}`);
      await errorReporter.reportAutopayError(errorObject, {
        action: 'subscription_cancel',
        subscription_id: subscriptionId,
        user_id: currentUser?.uid,
        error_details: error,
        severity: 'high'
      });
      
      setLoading(false);
      console.error("Subscription cancel failed", error);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: 300,
        textAlign: "center",
      }}
    >
      {pendingSubscription ? (
        <Typography>
          {" "}
          {`Autopay Scheduled.  Charges begin ${formatDate(pendingSubscription.startDate)}`}
        </Typography>
      ) : (
        <Typography>
          {activeSubscription?.canceledDate
            ? `Autopay Canceled.  Account remains active until ${formatDate(activeSubscription.canceledDate)}`
            : "Sumbit request to cancel Autopay"}
        </Typography>
      )}
      <br />

      <LoadingButton
        variant="contained"
        onClick={handleCancel}
        loading={loading}
        disabled={Boolean(activeSubscription?.canceledDate)}
      >
        Cancel Autopay
      </LoadingButton>
    </Box>
  );
};

const SubscriptionForm = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth); // Assuming you have a Firebase auth hook
  const currentUser = auth.currentUser;
  // const theme = useTheme();
  const [selectedCard, setSelectedCard] = React.useState("");
  const { savedCards, subscriptions } = useSelector((state) => state.user);
  const [localLoading, setLocalLoading] = React.useState(false);
  const { loading, error, lastPaymentId } = useSelector(
    (state) => state.payments,
  );

  const uniqueCards = React.useMemo(
    () => uniqBy(savedCards || [], "fingerprint"),
    [savedCards],
  );

  const handleCardTokenize = async (token) => {
    try {
      if (selectedCard || (token.status === "OK" && user)) {
        setLocalLoading(true);
      // Pass the token to your backend to create a customer and store card details
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net" +
          "/createCustomerAndSubscription",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await currentUser.getIdToken()}`, // Send Firebase token
          },
          body: JSON.stringify({
            sourceId: selectedCard || token.token, // Token from Square
            uid: currentUser.uid,
            locationId: process.env.REACT_APP_SQUARE_LOCATION_ID,
          }),
        },
      );

      if (response.ok) {
        const result = await response.json();
        dispatch(
          enqueueSnackbar({
            message: `Subscription setup successful: ${result}`,
            options: {
              variant: "success",
            },
          }),
        );
      } else {
        const error = await response.json();
        
        // Report autopay setup error to monitoring
        const errorObject = new Error(`Subscription setup failed: ${JSON.stringify(error)}`);
        await errorReporter.reportAutopayError(errorObject, {
          action: 'subscription_setup',
          user_id: currentUser?.uid,
          square_customer_id: user?.squareCustomerId,
          selected_card: selectedCard,
          error_details: error,
          severity: 'critical'
        });
        
        dispatch(
          enqueueSnackbar({
            message: `Subscription setup failed: ${JSON.stringify(error)}`,
            options: {
              variant: "error",
            },
          }),
        );
      }

      setLocalLoading(false);
    } else {
      // Report tokenization failure
      const errorObject = new Error('Tokenization failed or user not authenticated');
      await errorReporter.reportAutopayError(errorObject, {
        action: 'card_tokenization',
        user_id: currentUser?.uid,
        square_customer_id: user?.squareCustomerId,
        token_status: token?.status || 'unknown',
        severity: 'high'
      });
      
      dispatch(
        enqueueSnackbar({
          message: "Tokenization failed or user not authenticated",
          options: {
            variant: "error",
          },
        }),
      );
    }
    } catch (unexpectedError) {
      // Report any unexpected errors in autopay setup flow
      await errorReporter.reportAutopayError(unexpectedError, {
        action: 'autopay_setup_unexpected',
        user_id: currentUser?.uid,
        square_customer_id: user?.squareCustomerId,
        saved_cards_count: savedCards?.length || 0,
        unique_cards_count: uniqueCards?.length || 0,
        severity: 'critical'
      });
      
      setLocalLoading(false);
      dispatch(
        enqueueSnackbar({
          message: "An unexpected error occurred during autopay setup. Please try again.",
          options: {
            variant: "error",
          },
        }),
      );
    }
  };

  return find(
    subscriptions,
    (s) => s.status === "ACTIVE" || s.status === "PENDING",
  ) ? (
    <EndSubscription />
  ) : (
    <>
      {uniqueCards && uniqueCards.length > 0 && (
        <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
          <InputLabel id="saved-card-select-label">
            Select Saved Card
          </InputLabel>
          <Select
            labelId="saved-card-select-label"
            value={selectedCard}
            label="Select Saved Card"
            onChange={(e) => setSelectedCard(e.target.value)}
          >
            <MenuItem value="">Use New Payment Method</MenuItem>
            {uniqueCards.map((card) => (
              <MenuItem key={card.id} value={card.id}>
                {`${card.cardBrand} **** ${card.last4} (Exp: ${card.expMonth}/${card.expYear})`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {selectedCard ? (
        <LoadingButton
          fullWidth
          loading={loading || localLoading}
          variant="contained"
          color="primary"
          onClick={handleCardTokenize}
          sx={{
            p: 1.5,
            textTransform: "none",
            fontSize: "1rem",
          }}
        >
          Enable Autopay
        </LoadingButton>
      ) : (
        <PaymentForm
          applicationId={process.env.REACT_APP_SQUARE_APPLICATION_ID}
          locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
          cardTokenizeResponseReceived={handleCardTokenize}
        >
          <CreditCard>Enable Autopay</CreditCard>
        </PaymentForm>
      )}
    </>
  );
};

export default SubscriptionForm;
